'use client'

import { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useClientCountry } from '../i18n/ClientCountryProvider'
import useEventCountries from '@/hooks/cachedCalls/use-event-countries'
import CountrySelectionModal from '../layout/CountrySelectionModal'
import { useTranslation } from '@/hooks/useTranslation'

interface CountrySelectionModalContextType {
    openModal: () => void
    closeModal: () => void
    isOpen: boolean
}

const CountrySelectionModalContext = createContext<CountrySelectionModalContextType | undefined>(undefined)

export const useCountrySelectionModal = () => {
    const context = useContext(CountrySelectionModalContext)
    if (context === undefined) {
        throw new Error('useCountrySelectionModal must be used within a CountrySelectionModalProvider')
    }
    return context
}

interface CountrySelectionModalProviderProps {
    children: ReactNode
}

export const CountrySelectionModalProvider = ({ children }: CountrySelectionModalProviderProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const { currentCountryData, changeCountryData } = useClientCountry()
    const { data: countries } = useEventCountries()
    const { lang } = useTranslation()

    // Get locale for sorting countries alphabetically
    const getLocaleForSorting = (language: string): string => {
        const localeMap: Record<string, string> = {
            'es': 'es-ES',
            'en': 'en-GB',
            'pt': 'pt-BR',
            'ca': 'ca-ES',
            'eu': 'eu-ES',
            'fr': 'fr-FR',
            'it': 'it-IT'
        }
        return localeMap[language] || 'en-GB'
    }

    // Sort countries alphabetically by the selected language
    const sortedCountries = useMemo(() => {
        if (!countries) return []
        return [...countries].sort((a, b) => {
            return a.value.localeCompare(b.value, getLocaleForSorting(lang), { sensitivity: 'base' })
        })
    }, [countries, lang])

    const openModal = useCallback(() => {
        setIsOpen(true)
    }, [])

    const closeModal = useCallback(() => {
        setIsOpen(false)
    }, [])

    const handleCountrySelect = useCallback(async (value: string) => {
        const selectedCountry = countries?.find(country => country.id.toString() === value)
        if (selectedCountry) {
            changeCountryData({
                name: selectedCountry.value,
                id: selectedCountry.id.toString()
            })
            // Pequeño delay para asegurar que el estado se propague antes de cerrar el modal
            await new Promise(resolve => setTimeout(resolve, 100))
        }
    }, [countries, changeCountryData])

    const contextValue: CountrySelectionModalContextType = {
        openModal,
        closeModal,
        isOpen
    }

    return (
        <CountrySelectionModalContext.Provider value={contextValue}>
            {children}
            {/* Portal para renderizar el modal en el body */}
            {typeof window !== 'undefined' &&
                createPortal(
                    <CountrySelectionModal
                        isOpen={isOpen}
                        onClose={closeModal}
                        countries={sortedCountries}
                        currentCountryData={currentCountryData}
                        onCountrySelect={handleCountrySelect}
                    />,
                    document.body
                )}
        </CountrySelectionModalContext.Provider>
    )
}
